import * as React from "react";
import { AnimatePresence, motion } from "motion/react";
import type { AuthMotion } from "../../authMotion";
import { AuthField } from "../AuthField";
import { AuthButton } from "../AuthButton";

type Props = {
  id: string;
  inputRef: React.Ref<HTMLInputElement>;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: string | null;
  /** Highlight the field without a message (e.g. after a wrong-password toast). */
  invalid?: boolean;
  /** The email has been resolved; show it as a settled read-only row. */
  locked: boolean;
  onEdit: () => void;
  onForgotPassword: () => void;
  fade: AuthMotion["fade"];
};

/**
 * The email field. Once resolved it settles into a compact tinted row and the
 * "Forgot password?" action crossfades to "Edit".
 */
export function EmailStep({
  id,
  inputRef,
  value,
  onChange,
  onBlur,
  error,
  invalid,
  locked,
  onEdit,
  onForgotPassword,
  fade
}: Props) {
  return (
    <AuthField
      ref={inputRef}
      id={id}
      label="Email"
      name="email"
      type="email"
      inputMode="email"
      autoComplete="new-email"
      placeholder="you@firm.com"
      value={value}
      locked={locked}
      invalid={invalid}
      error={error}
      onChange={onChange}
      onBlur={onBlur}
      labelAction={
        // Old and new actions share one grid cell so they crossfade in place.
        <div className="grid justify-items-end *:col-start-1 *:row-start-1">
          <AnimatePresence initial={false}>
            {locked ? (
              <motion.span key="edit" {...fade}>
                <AuthButton variant="link" onClick={onEdit}>
                  Edit
                </AuthButton>
              </motion.span>
            ) : (
              <motion.span key="forgot" {...fade}>
                <AuthButton variant="link" onClick={onForgotPassword}>
                  Forgot Password?
                </AuthButton>
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      }
    />
  );
}
